/**
 * TransactionRenderer
 *
 * Manages ultra-high-performance transaction animations using typed arrays.
 * Renders 50,000+ transactions per second moving from spawn points to sequencer.
 *
 * CRITICAL PERFORMANCE FEATURES - DO NOT MODIFY:
 * - Float32Array/Uint32Array for zero garbage collection
 * - In-place array filtering (no allocations during animation)
 * - Speed-based movement (2.4 px/ms) instead of fixed duration
 * - Sprite pooling integration (no sprite creation during animation)
 * - Direct array access (no function call overhead)
 */

import { Container, Sprite, Texture } from 'pixi.js';
import {
  SCENE_WIDTH,
  SCENE_HEIGHT,
  MINIBLOCK_SIZE,
  TX_TRAVEL_TIME,
} from '../config/visualization.config';
import { getBulletColor } from '../utils/blockColors';

// Target point: Chart spawn point (where miniblocks spawn)
const CHART_SPAWN_X = SCENE_WIDTH / 2 - 99; // Chart left edge + 1px (chart spawns at SCENE_WIDTH/2 - 100)
const CHART_SPAWN_Y = 300; // Bottom of chart (baseline where bars spawn)

const BLOCKS_BOTTOM = 100 + 150; // Y position where blocks end

interface CircleData {
  x: number;
  y: number;
  radius: number;
}

export interface MiniblockCompletionCallback {
  (blockNumber: number, miniblockIndex: number): void;
}

export class TransactionRenderer {
  // Typed arrays - CRITICAL: Pre-allocated for zero garbage collection
  // OPTIMIZATION: Reduced from 20000 to 5000 for better performance on older devices
  private MAX_ACTIVE_TXS = 5000;
  private readonly INV_TX_TRAVEL_TIME = 1 / TX_TRAVEL_TIME; // Pre-calculated for performance (multiply is faster than divide)
  private txStartX: Float32Array;
  private txStartY: Float32Array;
  private txTargetX: Float32Array;
  private txTargetY: Float32Array;
  private txStartTime: Float32Array;
  private txDistance: Float32Array;
  private txBlockNumber: Uint32Array;
  private txMiniblockIndex: Uint32Array;
  private txColor: Uint32Array; // Color tint for each transaction (based on block)
  private txCount: number = 0;

  // Sprite pool reference
  private container: Container;
  private txSprites: Sprite[];

  // Track pending transactions per miniblock
  private pendingTxsPerMiniblock = new Map<string, number>(); // key: "blockNumber-index"

  // Circle renderer reference (for spawning from circles)
  private getActiveCircles: (() => CircleData[]) | null = null;

  // Spawn indicator reference (for bullet targeting)
  private getSpawnIndicatorFutureX: ((travelTime: number) => number) | null = null;
  private getSpawnIndicatorY: (() => number) | null = null;

  // Callback for miniblock completion
  private onMiniblockComplete: MiniblockCompletionCallback | null = null;

  // Callback for when transactions hit the sequencer (for spin acceleration)
  private onTransactionHit: ((count: number) => void) | null = null;

  // For alternating spawn positions across the front arc
  private spawnAngleIndex: number = 0;

  // Queue for miniblocks that completed this frame (ensures FIFO ordering)
  private completedThisFrame: Array<{ blockNum: number; mbIndex: number }> = [];

  // Stats tracking
  private txCountSinceLastUpdate = 0;

  constructor(container: Container, txSprites: Sprite[]) {
    this.container = container;
    this.txSprites = txSprites;

    // Pre-allocate typed arrays
    this.txStartX = new Float32Array(this.MAX_ACTIVE_TXS);
    this.txStartY = new Float32Array(this.MAX_ACTIVE_TXS);
    this.txTargetX = new Float32Array(this.MAX_ACTIVE_TXS);
    this.txTargetY = new Float32Array(this.MAX_ACTIVE_TXS);
    this.txStartTime = new Float32Array(this.MAX_ACTIVE_TXS);
    this.txDistance = new Float32Array(this.MAX_ACTIVE_TXS);
    this.txBlockNumber = new Uint32Array(this.MAX_ACTIVE_TXS);
    this.txMiniblockIndex = new Uint32Array(this.MAX_ACTIVE_TXS);
    this.txColor = new Uint32Array(this.MAX_ACTIVE_TXS);
  }

  /**
   * Set callback for when all transactions for a miniblock reach chart spawn point
   */
  public setMiniblockCompletionCallback(callback: MiniblockCompletionCallback): void {
    this.onMiniblockComplete = callback;
  }

  /**
   * Set callback for when transactions hit the sequencer (for spin acceleration)
   */
  public setTransactionHitCallback(callback: (count: number) => void): void {
    this.onTransactionHit = callback;
  }

  /**
   * Set reference to circle renderer for spawning from circles
   */
  public setCircleGetter(getter: () => CircleData[]): void {
    this.getActiveCircles = getter;
  }

  /**
   * Set reference to spawn indicator for bullet targeting
   */
  public setSpawnIndicatorGetters(
    futureXGetter: (travelTime: number) => number,
    yGetter: () => number
  ): void {
    this.getSpawnIndicatorFutureX = futureXGetter;
    this.getSpawnIndicatorY = yGetter;
  }

  /**
   * Get spawn position from a circle (on the perimeter facing the target)
   * Alternates back and forth across the front 180° arc
   */
  private getSpawnPositionFromCircle(circle: CircleData, targetX: number, targetY: number): { x: number; y: number } {
    // Calculate angle from circle center to target
    const toTargetAngle = Math.atan2(targetY - circle.y, targetX - circle.x);

    // Alternating pattern across the front arc
    // Use golden ratio to spread positions evenly without obvious patterns
    const goldenRatio = 1.618033988749;
    this.spawnAngleIndex++;

    // Golden ratio distribution creates even spread without repeating patterns
    const normalizedPos = (this.spawnAngleIndex * goldenRatio) % 1; // 0 to 1
    const angleOffset = (normalizedPos - 0.5) * Math.PI; // ±90°

    const angle = toTargetAngle + angleOffset;

    return {
      x: circle.x + Math.cos(angle) * circle.radius,
      y: circle.y + Math.sin(angle) * circle.radius
    };
  }

  /**
   * Calculate target point at chart spawn location
   * Uses spawn indicator's predicted future position if available
   */
  private getChartSpawnTarget(startX: number, startY: number): { x: number; y: number } {
    // Target the spawn indicator's predicted future position if available
    if (this.getSpawnIndicatorFutureX && this.getSpawnIndicatorY) {
      return {
        x: this.getSpawnIndicatorFutureX(TX_TRAVEL_TIME),
        y: this.getSpawnIndicatorY()
      };
    }

    // Fallback to static chart spawn point
    return { x: CHART_SPAWN_X, y: CHART_SPAWN_Y };
  }

  /**
   * Get spawn position for a transaction from a specific circle
   * Returns {startX, startY} on the perimeter of the circle facing the target
   */
  private getSpawnPosition(circle: CircleData, targetX: number, targetY: number): { startX: number; startY: number } {
    const pos = this.getSpawnPositionFromCircle(circle, targetX, targetY);
    return { startX: pos.x, startY: pos.y };
  }

  /**
   * Create transactions for a miniblock from specific circle positions
   * @param blockNumber - Block number this miniblock belongs to
   * @param miniblockIndex - Index of miniblock within block
   * @param circles - Array of circle positions for each transaction
   */
  public createTransactions(blockNumber: number, miniblockIndex: number, circles: CircleData[]): void {
    const count = circles.length;
    if (count === 0) return;

    const miniblockKey = `${blockNumber}-${miniblockIndex}`;
    this.pendingTxsPerMiniblock.set(miniblockKey, count);

    const now = performance.now();

    for (let i = 0; i < count; i++) {
      // Don't create if buffer is full (silently skip)
      if (this.txCount >= this.MAX_ACTIVE_TXS) {
        break;
      }

      // Get spawn position from the specific circle for this transaction
      const circle = circles[i];

      // Get target first (use circle center for initial calculation)
      const target = this.getChartSpawnTarget(circle.x, circle.y);

      // Now get spawn position on the front 180° facing the target
      const { startX, startY } = this.getSpawnPosition(circle, target.x, target.y);

      // Calculate total distance
      const dx = target.x - startX;
      const dy = target.y - startY;
      const totalDistance = Math.sqrt(dx * dx + dy * dy);

      // Write directly to typed arrays
      const idx = this.txCount;
      this.txStartX[idx] = startX;
      this.txStartY[idx] = startY;
      this.txTargetX[idx] = target.x;
      this.txTargetY[idx] = target.y;
      this.txDistance[idx] = totalDistance;
      // Add random offset (0-50ms) to stagger spawns within a miniblock
      // This creates a scattered stream effect instead of synchronized rings
      this.txStartTime[idx] = now - Math.random() * 50;
      this.txBlockNumber[idx] = blockNumber;
      this.txMiniblockIndex[idx] = miniblockIndex;
      this.txColor[idx] = getBulletColor(blockNumber); // Bright version of block color

      this.txCount++;
      this.txCountSinceLastUpdate++;
    }
  }

  /**
   * Create a single test transaction (for test mode)
   * Spawns from a random circle, or center if no circles available
   */
  public createTestTransaction(): void {
    // Don't create if buffer is full
    if (this.txCount >= this.MAX_ACTIVE_TXS) return;

    const now = performance.now();

    // Get a random circle or use fallback position
    const activeCircles = this.getActiveCircles ? this.getActiveCircles() : [];
    let startX: number, startY: number;

    if (activeCircles.length > 0) {
      const circle = activeCircles[Math.floor(Math.random() * activeCircles.length)];
      // Get target first, then spawn from front 180° facing target
      const target = this.getChartSpawnTarget(circle.x, circle.y);
      const pos = this.getSpawnPosition(circle, target.x, target.y);
      startX = pos.startX;
      startY = pos.startY;
    } else {
      // Fallback to center if no circles
      startX = SCENE_WIDTH / 2;
      startY = SCENE_HEIGHT / 2;
    }

    // Get target point at chart spawn location
    const target = this.getChartSpawnTarget(startX, startY);

    // Calculate total distance
    const dx = target.x - startX;
    const dy = target.y - startY;
    const totalDistance = Math.sqrt(dx * dx + dy * dy);

    // Write directly to typed arrays (blockNumber=0 means test transaction)
    const idx = this.txCount;
    this.txStartX[idx] = startX;
    this.txStartY[idx] = startY;
    this.txTargetX[idx] = target.x;
    this.txTargetY[idx] = target.y;
    this.txDistance[idx] = totalDistance;
    this.txStartTime[idx] = now;
    this.txBlockNumber[idx] = 0; // 0 means test transaction
    this.txMiniblockIndex[idx] = 0;
    // Use random block color for test transactions for variety
    this.txColor[idx] = getBulletColor(Math.floor(Math.random() * 12));

    this.txCount++;
    this.txCountSinceLastUpdate++;
  }

  /**
   * Main animation update loop - CRITICAL PERFORMANCE PATH
   * @param delta - Delta time from Pixi ticker (not used, we use performance.now() instead)
   * @returns number of transactions created since last TPS update
   */
  public update(delta: number): number {
    const now = performance.now();

    // Process all active transactions with pre-allocated arrays
    let writeIndex = 0;
    let spriteIndex = 0;
    let completedTxCount = 0; // Track transactions hitting sequencer this frame

    for (let i = 0; i < this.txCount; i++) {
      const elapsed = now - this.txStartTime[i];

      // Check if transaction is complete (all transactions take TX_TRAVEL_TIME ms)
      if (elapsed >= TX_TRAVEL_TIME) {
        completedTxCount++; // Count transaction hitting sequencer

        // Handle miniblock creation for non-test transactions
        const blockNum = this.txBlockNumber[i];
        const mbIndex = this.txMiniblockIndex[i];

        if (blockNum !== 0) { // 0 means test transaction
          const miniblockKey = `${blockNum}-${mbIndex}`;
          const pending = this.pendingTxsPerMiniblock.get(miniblockKey) || 0;

          if (pending > 0) {
            const newPending = pending - 1;
            this.pendingTxsPerMiniblock.set(miniblockKey, newPending);

            // When all txs reach sequencer, queue for callback (will fire in order after loop)
            if (newPending === 0) {
              this.pendingTxsPerMiniblock.delete(miniblockKey);
              this.completedThisFrame.push({ blockNum, mbIndex });
            }
          }
        }

        continue; // Skip expired transactions
      }

      // Calculate progress based on elapsed time (all txs take same time)
      const progress = elapsed * this.INV_TX_TRAVEL_TIME;

      // Calculate current position (inline to avoid function calls)
      const dx = this.txTargetX[i] - this.txStartX[i];
      const dy = this.txTargetY[i] - this.txStartY[i];
      const currentX = this.txStartX[i] + dx * progress;
      const currentY = this.txStartY[i] + dy * progress;

      // Update sprite position and color
      const sprite = this.txSprites[spriteIndex];
      sprite.x = currentX;
      sprite.y = currentY;
      sprite.tint = this.txColor[i]; // Apply block-based color
      sprite.visible = true;
      spriteIndex++;

      // Compact array - move this transaction to write position (zero-allocation filtering)
      if (writeIndex !== i) {
        this.txStartX[writeIndex] = this.txStartX[i];
        this.txStartY[writeIndex] = this.txStartY[i];
        this.txTargetX[writeIndex] = this.txTargetX[i];
        this.txTargetY[writeIndex] = this.txTargetY[i];
        this.txStartTime[writeIndex] = this.txStartTime[i];
        this.txDistance[writeIndex] = this.txDistance[i];
        this.txBlockNumber[writeIndex] = this.txBlockNumber[i];
        this.txMiniblockIndex[writeIndex] = this.txMiniblockIndex[i];
        this.txColor[writeIndex] = this.txColor[i];
      }
      writeIndex++;
    }

    this.txCount = writeIndex;

    // Process completed miniblocks in order (block number, then index)
    if (this.completedThisFrame.length > 0) {
      // Sort by block number first, then by miniblock index
      this.completedThisFrame.sort((a, b) => {
        if (a.blockNum !== b.blockNum) return a.blockNum - b.blockNum;
        return a.mbIndex - b.mbIndex;
      });

      // Fire callbacks in order
      for (const completed of this.completedThisFrame) {
        if (this.onMiniblockComplete) {
          this.onMiniblockComplete(completed.blockNum, completed.mbIndex);
        }
      }

      // Clear queue for next frame (reuse array to avoid allocation)
      this.completedThisFrame.length = 0;
    }

    // Notify sequencer about transaction hits (for spin acceleration)
    if (completedTxCount > 0 && this.onTransactionHit) {
      this.onTransactionHit(completedTxCount);
    }

    // Hide unused sprites
    for (let i = spriteIndex; i < this.MAX_ACTIVE_TXS; i++) {
      if (this.txSprites[i].visible) {
        this.txSprites[i].visible = false;
      }
    }

    // Return count for TPS calculation and reset
    const txCreated = this.txCountSinceLastUpdate;
    this.txCountSinceLastUpdate = 0;
    return txCreated;
  }

  /**
   * Get current number of active transactions
   */
  public getActiveCount(): number {
    return this.txCount;
  }

  /**
   * Clear all transactions and reset state
   */
  public clear(): void {
    this.txCount = 0;
    this.txCountSinceLastUpdate = 0;
    this.pendingTxsPerMiniblock.clear();

    // Hide all sprites
    for (let i = 0; i < this.MAX_ACTIVE_TXS; i++) {
      this.txSprites[i].visible = false;
    }
  }

  /**
   * Cleanup when destroying the renderer
   */
  public destroy(): void {
    this.clear();
    // Note: Sprites are owned by PixiAppSetup, don't destroy them here
  }
}
